import React from "react";
import { Link } from "react-router-dom";

/**
 * FieldButton — brutalist mono-label CTA button.
 *
 * - `variant="solid"` → white fill, ink text (primary action)
 * - `variant="ghost"` → transparent fill, white border + text (secondary)
 *
 * Accepts either `to` (internal Link), `href` (external/anchor), or `onClick`.
 */
export const FieldButton = ({
    children,
    variant = "ghost",
    to,
    href,
    onClick,
    type = "button",
    disabled,
    fullWidth,
    size = "default",
    "data-testid": testId,
    style,
    ...rest
}) => {
    const isSolid = variant === "solid";
    const padding = size === "lg" ? "16px 32px" : size === "sm" ? "10px 18px" : "14px 26px";

    const baseStyle = {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        width: fullWidth ? "100%" : undefined,
        background: disabled
            ? "rgba(255,255,255,0.18)"
            : isSolid
              ? "var(--on-ink)"
              : "transparent",
        color: disabled
            ? "var(--on-ink-mute)"
            : isSolid
              ? "var(--ink)"
              : "var(--on-ink)",
        border: isSolid ? "none" : "1px solid var(--on-ink-line-strong, rgba(255,255,255,0.45))",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.28em",
        textTransform: "uppercase",
        padding,
        cursor: disabled ? "not-allowed" : "pointer",
        textDecoration: "none",
        transition: "background 0.3s, color 0.3s, border-color 0.3s",
        ...style,
    };

    const content = children;

    if (to && !disabled) {
        return (
            <Link to={to} onClick={onClick} style={baseStyle} data-testid={testId} {...rest}>
                {content}
            </Link>
        );
    }
    if (href && !disabled) {
        return (
            <a href={href} onClick={onClick} style={baseStyle} data-testid={testId} {...rest}>
                {content}
            </a>
        );
    }
    return (
        <button
            type={type}
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            style={baseStyle}
            data-testid={testId}
            {...rest}
        >
            {content}
        </button>
    );
};

export default FieldButton;
